import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import {
  Form,
  Select,
  Button,
  Modal,
  DatePicker,
  Checkbox,
  Input,
  Tooltip
} from 'antd';
import Icon from '@ant-design/icons';
import dayjs from 'dayjs';
import classNames from 'classnames';

import { cleanEmptyString, checkDate, isSllrUser } from 'utils/helpers';
import { ModalContext, useModal } from 'contexts/modalProvider.context';
import { disabledDate } from 'utils/pickups';
import { sendSegment } from 'utils/segment';
import { minNumberRule } from 'utils/forms';
import { userHasTabAccess } from 'utils/permissions/permissions-apply';

import {
  fetchPickupLocations,
  fetchPickupLocation,
  createPickup,
  editPickup,
  fetchPickup,
  getHolidays
} from 'services/pickups';
import { checkIfBulkyDeliveriesExist } from 'services/shipments';
import {
  PICKUP_DATE_QUICK_FILTERS,
  DAYS,
  PICKUP_PACKAGE_TYPE,
  RECURRING_PICKUP_BUSINESS_TIERS,
  REPEATED_TYPE_SELECTION,
  DATE_FORMAT,
  DATE_FORMAT_WITH_DAY,
  CANCELED_PICKUP_CONFIGURATION_KEY,
  MINIMUM_VALUE_FOR_PARCELS_NUMBER
} from 'constants/pickups';
import { LOCALE } from 'constants/intl-wrapper';
import { getCurrentUserCountryData } from 'constants/countries/countries-mapping';
import { PERMISSIONS_MODULES_ID } from 'constants/team-members';

import { notify } from 'components/Notify/Notify';
import CreatePickupLocationFormModal from 'components/Pickups/components/CreatePickupLocationFormModal/CreatePickupLocationFormModal';
import CreateContactPersonFormModal from 'components/Pickups/components/CreateContactPersonFormModal/CreateContactPersonFormModal';
import BRContentHeader from 'components/BRContentHeader/BRContentHeader';
import BRButton from 'components/BRButton/BRButton';
import ActivationModal from 'components/BRModals/ActivationModal/ActivationModal';
import SchedulingPickupModal from 'components/BRModals/SchedulingPickupModal/SchedulingPickupModal';
import BRInfoBanner from 'components/BRInfoBanner/BRInfoBanner';
import CreateOrderModal from '../CreateOrderModal/CreateOrderModal';
import CancelledPickupsBanner from '../CancelledPickupsBanner/CancelledPickupsBanner';

import { ReactComponent as DatePickerIcon } from 'assets/bosta-icons/Calendar.svg';
import { ReactComponent as PlusIcon } from 'assets/bosta-icons/Plus.svg';
import { ReactComponent as ArrowRightIcon } from 'assets/bosta-icons/Arrow-right.svg';
import { ReactComponent as Rocket } from 'assets/bosta-icons/rocket.svg';

import './CreateEditPickupModal.less';

class CreateEditPickupModal extends Component {
  constructor(props) {
    super(props);
    CreateEditPickupModal.contextType = ModalContext;
  }

  state = {
    pickupLocations: [],
    isLoading: false,
    pickupLocation: null,
    scheduledDate: null,
    repeatedPickup: {
      isRepeated: false,
      days: [],
      endDate: null
    },
    contactPerson: {},
    fullContactPersonData: {},
    onBoarding: false,
    createNewContact: false,
    isNewZoning: true,
    locationId: null,
    cancelUpdate: false,
    withNotes: false,
    bulkyOptions: {
      lightBulky: false,
      heavyBulky: false
    },
    showRepeatedPickups: false,
    isSchedulingPickupModalVisible: false,
    openedFromScedulingPickModal: false,
    isPrepareOrdersBannerVisible: false,
    holidaysDates: []
  };

  formRef = React.createRef();
  datePickerRef = React.createRef();

  handleDateAccordingToServer = (data) => {
    const { serverTimeData } = this.props;
    if (data?.value) {
      const { value } = data;
      return serverTimeData?.timeZone?.utcOffset
        ? dayjs(value).utcOffset(serverTimeData?.timeZone?.utcOffset)
        : dayjs(value);
    }
    return serverTimeData?.timeZone?.utcOffset
      ? dayjs().utcOffset(serverTimeData?.timeZone?.utcOffset)
      : dayjs();
  };

  resolvePickupCancelationAction = (locationId) => {
    const { businessInfo, handleUpdateFeaturesConfigurations } = this.context;

    const canceledPickups =
      businessInfo?.featuresConfigurations?.canceledPickupsWaitingForAction ||
      [];

    const hasUnresolvedCancelationAction = canceledPickups.some(
      (pickup) => pickup.businessLocationId !== locationId
    );

    if (!hasUnresolvedCancelationAction) {
      return;
    }

    handleUpdateFeaturesConfigurations(
      CANCELED_PICKUP_CONFIGURATION_KEY,
      canceledPickups.filter(
        (pickup) => pickup.businessLocationId !== locationId
      )
    );
  };

  onFinish = async (values) => {
    const { openModal, businessInfo, setBusinessInfo } = this.context;
    this.setState({ isLoading: true });
    const { isUserFullyActivated } = JSON.parse(
      localStorage.getItem('userInfo')
    )?.user;

    const { intl } = this.props;

    if (!isUserFullyActivated) {
      this.setState({ isLoading: false });
      openModal(ActivationModal, {
        icon: <Icon component={Rocket} />,
        title: intl.formatMessage({
          id: 'new_header.orders.activation_modal.title'
        }),
        subtitle: intl.formatMessage({
          id: 'new_header.orders.activation_modal.subtitle'
        }),
        buttonText: intl.formatMessage({
          id: 'new_header.orders.activation_modal.buttonText'
        })
      });
    } else {
      const { close, onSuccess = () => {}, intl } = this.props;
      const {
        scheduledDate,
        fullContactPersonData,
        cancelUpdate,
        bulkyOptions,
        repeatedPickup
      } = this.state;
      if (!cancelUpdate) {
        delete fullContactPersonData?.firstName;
        delete fullContactPersonData?.lastName;
        delete fullContactPersonData?.isDefault;
        const payload = {
          businessLocationId: values?.locationId,
          contactPerson: cleanEmptyString(fullContactPersonData),
          scheduledDate: dayjs(scheduledDate).format('YYYY-MM-DD'),
          notes: values?.notes,
          ...(values?.isBulkyPickup && {
            packageType: bulkyOptions?.heavyBulky
              ? PICKUP_PACKAGE_TYPE.HEAVY_BULKY
              : PICKUP_PACKAGE_TYPE.LIGHT_BULKY
          }),
          ...(repeatedPickup?.isRepeated
            ? {
                repeatedData: {
                  repeatedType:
                    REPEATED_TYPE_SELECTION[
                      repeatedPickup?.days?.length === 6 ? 0 : 1
                    ].value,
                  days: repeatedPickup?.days.map(
                    (dayIndex) => DAYS[dayIndex].value
                  ),
                  startDate: scheduledDate,
                  endDate: repeatedPickup?.endDate
                }
              }
            : {
                repeatedData: {
                  repeatedType: 'One Time'
                }
              }),
          ...(values.subAccountId && { subAccountId: values.subAccountId }),
          ...(values.numberOfParcels && {
            numberOfParcels: values.numberOfParcels
          })
        };
        try {
          this.setState({ isLoading: true });
          if (this.props.pickupId) {
            delete payload.contactPerson._id;
            const pickupId = this.props.pickupId;
            await editPickup({ id: pickupId, payload });
            sendSegment('E_PICKUP_EDITED_COMPLETED', {
              // 'Edited Field Name': changedFields,
              // 'Matching Hub': pickupMatchedHub,
              'Pickup Request ID': pickupId
            });
            notify({
              msg: intl.formatMessage({
                id: `pickups.create_edit_pickup.edit_pickup_success`
              }),
              type: 'success'
            });
            close();
            onSuccess();
          } else {
            const { puid } = await createPickup({
              ...payload,
              creationSrc: window.screen.width > 992 ? 'Web' : 'Web Responsive',
              sessionId: localStorage.getItem('ajs_anonymous_id')
            });
            this.resolvePickupCancelationAction(values?.locationId);
            sendSegment('E_PICKUP_CREATED', {
              'is Heavy Bulky':
                bulkyOptions?.heavyBulky && values?.isBulkyPickup
                  ? 'Yes'
                  : 'No',
              'is Light Bulky':
                bulkyOptions?.lightBulky && values?.isBulkyPickup ? 'Yes' : 'No'
            });
            if (repeatedPickup?.isRepeated) {
              sendSegment('E_PICKUP_REPEATED', {
                repeatedType:
                  REPEATED_TYPE_SELECTION[
                    repeatedPickup?.days?.length === 6 ? 0 : 1
                  ].value,
                days: repeatedPickup.days,
                endDate: repeatedPickup.endDate,
                startDate: scheduledDate
              });
            }
            if (!businessInfo?.isFirstPickupCreated) {
              const userInfo = JSON.parse(localStorage.getItem('userInfo'));
              userInfo.user.isFirstPickupCreated = true;
              localStorage.setItem('userInfo', JSON.stringify(userInfo));
              sendSegment('Step3_First-pickup-created');
              sendSegment('Step4_First-order-completed-successfully');
              sendSegment('Step7_First-pickup-scheduled-successfully', {
                Account_Type: isSllrUser() ? 'Sllr' : 'Bosta'
              });
              if (isSllrUser()) {
                setBusinessInfo({
                  ...businessInfo,
                  isFirstPickupCreated: true
                });
              }
            }
            notify({
              msg: intl.formatMessage({
                id: `new_order.request_pickup.pickup_created_successfully`
              }),
              type: 'success'
            });
            close();
            onSuccess(puid);
          }
        } catch (error) {
          notify({ msg: error.message, error });
        }
        this.setState({ isLoading: false });
      } else {
        notify({
          msg: intl.formatMessage({
            id: `pickups.create_edit_pickup.error_create_new_zoning`
          })
        });
      }
    }
    this.setState({ isLoading: false });
  };

  getPickupLocations = async () => {
    try {
      const { onBoarding, locationId, isNewZoning } = this.state;
      this.setState({ isLoading: true });
      const { list } = await fetchPickupLocations();

      this.setState({
        pickupLocations: list,
        isLoading: false
      });
      if (!onBoarding) {
        this.selectedValueOnboarding();
      } else {
        if (this.props.pickupId) {
          return;
        }
        const id = list.find((item) => item.isDefault)?._id;
        this.getPickupLocation(id);
        this.formRef.current?.setFieldsValue({
          locationId: isNewZoning ? id : locationId
        });
      }
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  fetchHolidays = async () => {
    try {
      const payload = {
        countryCode: getCurrentUserCountryData().codeName
      };

      const data = await getHolidays(payload);

      const holidaysDates = data.map(({ holidayDate }) =>
        dayjs(holidayDate, 'DD-MM-YYYY')
      );

      this.setState({
        holidaysDates
      });
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  checkPickupLocation = (PickupLocation) => {
    const { openModal } = this.context;
    if (PickupLocation?.isNewZoning) {
      this.setState({ isNewZoning: true });
    } else {
      this.setState({ isNewZoning: false });
      openModal(CreatePickupLocationFormModal, {
        pickupLocation: PickupLocation,
        onSuccess: () => {
          this.setState({ locationId: PickupLocation._id }, () => {
            this.getPickupLocations();
          });
        },
        onCancel: () => {
          this.setState({ cancelUpdate: true });
        }
      });
    }
  };

  getPickupLocation = async (locationId, contactPersonName) => {
    try {
      this.setState({ isLoading: true });
      const data = await fetchPickupLocation(locationId);
      this.setState({ pickupLocation: data });

      this.checkPickupLocation(data);
      this.formRef.current?.setFieldsValue({
        contactPerson: contactPersonName
          ? contactPersonName
          : data?.contacts[data?.contacts?.length - 1]?.name
      });
      this.setState({
        isLoading: false,
        fullContactPersonData: cleanEmptyString(
          data?.contacts[data?.contacts?.length - 1]
        )
      });
      return data;
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  handlePickupLocationChange = (locationId) => {
    this.getPickupLocation(locationId);
    const { pickupLocation } = this.state;
    this.formRef.current.setFieldsValue({
      locationId,
      contactPerson: pickupLocation?.contacts[0]?.name
    });
  };

  handleOnChangePickupDate = (scheduledDate) => {
    this.setState({
      scheduledDate,
      repeatedPickup: {
        ...this.state.repeatedPickup,
        endDate: scheduledDate.add(4, 'week')
      }
    });
  };

  handleOnChangeEndDate = (endDate) => {
    this.setState({
      repeatedPickup: {
        ...this.state.repeatedPickup,
        endDate
      }
    });
  };

  onChangeContactPerson = (contactPerson) => {
    const { pickupLocation } = this.state;
    let newPerson;
    pickupLocation.contacts.map((person) => {
      if (person?.name === contactPerson) {
        newPerson = person.name;
        this.setState({
          contactPerson: newPerson,
          fullContactPersonData: person
        });
      }

      return null;
    });
  };

  selectedValueOnboarding() {
    const { pickupLocations } = this.state;
    const id = pickupLocations.find((item) => item.isDefault)?._id;
    this.getPickupLocation(id);

    this.formRef.current.setFieldsValue({
      locationId: id,
      scheduledDate: dayjs.utc(new Date()).add(1, 'days')
    });
    this.setState({ scheduledDate: dayjs.utc(new Date()).add(1, 'days') });
  }

  handleDatePickerFooterClick = (daysToAdd) => {
    this.formRef.current.setFieldsValue({
      scheduledDate: dayjs.utc(new Date()).add(daysToAdd, 'days')
    });
    this.setState({
      scheduledDate: dayjs.utc(new Date()).add(daysToAdd, 'days')
    });
    this.datePickerRef.current.blur();
  };

  datePickerFooter = () => {
    //index is equal to the number of days to be added because the filters are today and tomorrow i.e add 0 and 1 days respectively to today's date
    const { scheduledDate } = this.state;
    const { pickupCutoffTime } = this.props;

    return (
      <div className="br-request-pickup-modal__date-picker__footer">
        {PICKUP_DATE_QUICK_FILTERS.map(
          ({ label, valueInDays }) =>
            (valueInDays || dayjs().hour() < pickupCutoffTime) && (
              <Button
                key={valueInDays}
                type={
                  dayjs(scheduledDate).isSame(
                    dayjs().add(valueInDays, 'days'),
                    'days'
                  ) && 'primary'
                }
                className="br-request-pickup-modal__date-picker__footer-btn"
                onClick={() => {
                  this.handleDatePickerFooterClick(valueInDays);
                }}
              >
                {label}
              </Button>
            )
        )}
      </div>
    );
  };

  datePickerCellRender = (current) => {
    const { intl } = this.props;
    const { pickupCutoffTime, serverTimeData } = this.context;
    const formattedUserHour = dayjs().utcOffset(
      serverTimeData?.timeZone?.utcOffset || 0
    );
    const currentDayOfMonth = this.handleDateAccordingToServer().get('date');

    const formattedCurrent = this.handleDateAccordingToServer({
      value: current
    }).get('date');

    if (
      formattedCurrent === currentDayOfMonth &&
      formattedUserHour >= pickupCutoffTime
    ) {
      return (
        <Tooltip
          getPopupContainer={(trigger) =>
            trigger.parentElement.parentElement.parentElement.parentElement
              .parentElement.parentElement.parentElement
          }
          title={intl.formatMessage(
            {
              id: 'new_order.request_pickup_modal.disabled_date_tooltip'
            },
            {
              hour:
                pickupCutoffTime > 12
                  ? pickupCutoffTime - 12
                  : pickupCutoffTime || 12
            }
          )}
        >
          <div className="ant-picker-cell-inner br-request-pickup-modal__form__date-picker-dropdown__with-tooltip">
            {this.handleDateAccordingToServer({
              value: current
            }).get('date')}
          </div>
        </Tooltip>
      );
    } else {
      return <div className="ant-picker-cell-inner">{current.date()}</div>;
    }
  };

  handleRepeatedPickupDayChange = (dayIndex) => {
    const {
      repeatedPickup: { days }
    } = this.state;
    const newDaysArray = days;
    const indexOfSelectedDay = days.findIndex((value) => value === dayIndex);
    if (indexOfSelectedDay >= 0 && days.length > 1) {
      delete newDaysArray[indexOfSelectedDay];
    } else {
      indexOfSelectedDay < 0 && newDaysArray.push(dayIndex);
    }
    this.setState({
      repeatedPickup: {
        ...this.state.repeatedPickup,
        days: newDaysArray.filter((n) => n !== undefined)
      }
    });
  };

  checkBulkyOption = async () => {
    try {
      const { hasHeavyBulkyType, hasLightBulkyType } =
        await checkIfBulkyDeliveriesExist();
      if (hasHeavyBulkyType) {
        this.setState({
          bulkyOptions: {
            heavyBulky: true
          }
        });
      } else if (!hasHeavyBulkyType && hasLightBulkyType) {
        this.setState({
          bulkyOptions: {
            lightBulky: true
          }
        });
      }
      this.formRef.current?.setFieldsValue({
        isBulkyPickup: hasHeavyBulkyType || hasLightBulkyType
      });
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  checkShowRepeatedPickups = () => {
    const {
      businessInfo: { businessTier, allowRecurringPickups }
    } = this.context;

    this.setState({
      showRepeatedPickups:
        RECURRING_PICKUP_BUSINESS_TIERS.includes(businessTier) ||
        allowRecurringPickups
    });
  };

  handleDaysToIndex = (days) => {
    const indexDays = [];
    days.forEach((day) => {
      indexDays.push(DAYS.findIndex(({ value }) => value === day));
    });
    return indexDays;
  };

  getPickupInfo = async () => {
    const { isNewZoning } = this.state;
    try {
      const { pickupId } = this.props;
      const {
        businessLocationId,
        scheduledDate,
        contactPerson: { name, phone, secPhone, email },
        repeatedData,
        notes,
        numberOfParcels
      } = await fetchPickup(pickupId);
      this.getPickupLocation(businessLocationId, name);
      const date = dayjs.utc(new Date(scheduledDate));
      this.formRef.current?.setFieldsValue({
        locationId: isNewZoning ? businessLocationId : null,
        scheduledDate: date,
        contactPerson: name,
        phone,
        secPhone,
        email,
        notes,
        numberOfParcels
      });
      this.handleOnChangePickupDate(date);
      const endDate = dayjs.utc(new Date(repeatedData?.endDate));
      const isFriday = date.get('day') === 5;
      this.setState({
        repeatedPickup: {
          ...this.state.repeatedPickup,
          isRepeated: repeatedData?.repeatedType !== 'One Time',
          days:
            repeatedData?.days?.length > 0
              ? this.handleDaysToIndex(repeatedData?.days)
              : [isFriday ? 0 : (date.get('day') + 1) % 7],
          endDate: endDate.isValid() ? endDate : dayjs.utc(date.add(4, 'week'))
        }
      });
      if (notes && notes !== 'N/A') {
        this.setState({ withNotes: true });
      }
    } catch (error) {
      notify({ msg: error.message, error });
    }
  };

  doOnMount = () => {
    const isFirstOrderCreated = JSON.parse(localStorage.getItem('userInfo'))
      .user.isFirstOrderCreated;
    const { pickupCutoffTime } = this.context;
    const { holidaysDates } = this.state;

    this.setState(
      {
        onBoarding:
          isFirstOrderCreated === undefined || isFirstOrderCreated
            ? true
            : isFirstOrderCreated
      },
      () => {
        this.getPickupLocations();
        this.fetchHolidays();
        this.checkBulkyOption();
        this.checkShowRepeatedPickups();
      }
    );

    if (isFirstOrderCreated === undefined || isFirstOrderCreated) {
      const formattedUserTime = this.handleDateAccordingToServer();
      const scheduledDate = disabledDate(
        formattedUserTime,
        formattedUserTime,
        pickupCutoffTime,
        holidaysDates
      )
        ? this.handleDateAccordingToServer().add(1, 'days')
        : this.handleDateAccordingToServer();
      this.formRef.current?.setFieldsValue({
        scheduledDate: scheduledDate
      });

      //dayjs get days method is shifted by 1 so sunday is day at index 0 instead of 1 compared to the DAYS constant imported so we add 1 and mod by 7 for saturday
      //at index 6 to map to (1+6)%7 = 0

      const isFriday = scheduledDate.get('day') === 5;
      if (!this.props.pickupId) {
        this.setState({
          scheduledDate: scheduledDate,
          repeatedPickup: {
            ...this.state.repeatedPickup,
            days: [isFriday ? 0 : (scheduledDate.get('day') + 1) % 7],
            endDate: scheduledDate.add(4, 'week')
          }
        });
      }
    }
    if (this.props.pickupId) {
      this.getPickupInfo();
    }
  };

  componentDidMount() {
    const canCreatePickup = JSON.parse(localStorage.getItem('canCreatePickup'));
    const { businessInfo, hideSchedulingPickupModal, openModal } =
      this.context;
    const { close } = this.props;
    const isFirstPickupCreated = businessInfo?.isFirstPickupCreated;

    if (!canCreatePickup) {
      openModal(CreateOrderModal);
      return close();
    }

    this.setState({
      isSchedulingPickupModalVisible: hideSchedulingPickupModal
        ? !hideSchedulingPickupModal
        : !isFirstPickupCreated,
      isPrepareOrdersBannerVisible:
        !isFirstPickupCreated &&
        !businessInfo?.featuresConfigurations?.isPrepareOrdersBannerDismissed
    });

    if (!businessInfo?.isFirstPickupCreated && !hideSchedulingPickupModal) {
      this.handleOpenSchedulingPickupModal();
    } else {
      this.doOnMount();
    }
  }

  handleOpenSchedulingPickupModal = () => {
    const { openModal, businessInfo } = this.context;

    this.setState({
      isSchedulingPickupModalVisible: !businessInfo?.isFirstPickupCreated
    });
    openModal(SchedulingPickupModal, {
      closeParentModal: this.props.close,
      backToParentModal: () => {
        this.setState({
          isSchedulingPickupModalVisible: false,
          openedFromScedulingPickModal: true
        });
        this.doOnMount();
      }
    });
  };

  handleCreateContactPersonClick = () => {
    const { businessInfo } = this.context;
    const { intl } = this.props;
    const { pickupLocation } = this.state;
    const { openModal } = this.context;

    if (pickupLocation?.canEdit) {
      openModal(CreateContactPersonFormModal, {
        pickupLocation,
        getPickupLocation: this.getPickupLocation,
        businessInfo
      });
    } else {
      notify({
        msg: intl.formatMessage({
          id: !this.formRef.current.getFieldValue('locationId')
            ? 'settings.pickup_locations.pickup_location_form.add_contact_before_location_error'
            : 'settings.pickup_locations.pickup_location_form.add_contact_error'
        }),
        status: -1
      });
    }
  };

  handleAddPickupLocationClick = (e) => {
    const { openModal } = this.context;
    openModal(CreatePickupLocationFormModal, {
      onSuccess: (pickupLocationId) => {
        this.getPickupLocations(false);
        this.handlePickupLocationChange(pickupLocationId);
      }
    });
  };

  customSelectDropdownFooter = () => {
    const { intl, businessInfo } = this.props;

    const canAccessSettings = userHasTabAccess(
      PERMISSIONS_MODULES_ID.SETTINGS,
      businessInfo?.userSllrPermissions
    );

    return (
      canAccessSettings && (
        <div className="br-request-pickups-modal__select-dropdown-footer display-flex body">
          <div className="br-request-pickups-modal__select-dropdown-footer-title">
            {intl.formatMessage({
              id: `new_order.request_pickup_modal.select_footer`
            })}
          </div>
          <BRButton
            type="link-color"
            label={intl.formatMessage({ id: 'nav_bar.settings' })}
            className="button-md"
            suffixIcon={<ArrowRightIcon />}
            onClick={() =>
              window.open('/settings/business-locations', '_blank')
            }
          />
        </div>
      )
    );
  };

  renderLocationDropdown = (menu) => {
    const { intl } = this.props;

    return (
      <>
        <div className="br-request-pickups-modal__select-dropdown-header">
          <BRButton
            type="link-color"
            label={intl.formatMessage({
              id: `pickups.actions.add_location`
            })}
            className="button-md"
            prefixIcon={<PlusIcon />}
            onClick={this.handleAddPickupLocationClick}
          />
        </div>
        {menu}
        {this.customSelectDropdownFooter()}
      </>
    );
  };

  renderContactPersonDropdown = (menu) => {
    const { intl } = this.props;
    return (
      <>
        <div className="br-request-pickups-modal__select-dropdown-header">
          <BRButton
            type="link-color"
            label={intl.formatMessage({
              id: `pickups.actions.add_person`
            })}
            className="button-md"
            prefixIcon={<PlusIcon />}
            onClick={this.handleCreateContactPersonClick}
          />
        </div>
        {menu}
        {this.customSelectDropdownFooter()}
      </>
    );
  };

  handlePrepareOrdersGuide = ({ isFromPickupModal = true }) => {
    const { handleOpenPrepareOrderModal, openModal } = this.context;
    const { close, history } = this.props;

    close();
    handleOpenPrepareOrderModal({
      history,
      source: 'PickupPage',
      isFromPickupModal: isFromPickupModal,
      openModal
    });
  };

  handleDismissInfoBanner = async () => {
    this.setState({ isLoading: true });
    const { handleUpdateFeaturesConfigurations } = this.props;
    await handleUpdateFeaturesConfigurations('isPrepareOrdersBannerDismissed');
    this.setState({ isLoading: false });
  };

  handleCancel = () => {
    const { close, hideSchedulingPickupModal } = this.props;
    const { openedFromScedulingPickModal } = this.state;

    openedFromScedulingPickModal || hideSchedulingPickupModal
      ? this.handleOpenSchedulingPickupModal()
      : close();
  };

  pickupDisabledDates = (current) => {
    const { holidaysDates } = this.state;
    const { pickupCutoffTime } = this.props;

    const formattedUserTime = this.handleDateAccordingToServer();
    return disabledDate(
      current,
      formattedUserTime,
      pickupCutoffTime,
      holidaysDates
    );
  };

  render() {
    const { intl, pickupId } = this.props;
    const {
      pickupLocations,
      pickupLocation,
      isLoading,
      onBoarding,
      repeatedPickup,
      withNotes,
      bulkyOptions,
      showRepeatedPickups,
      isSchedulingPickupModalVisible,
      scheduledDate,
      isPrepareOrdersBannerVisible
    } = this.state;
    const { businessInfo } = this.context;

    const isUserFullyActivated = JSON.parse(localStorage.getItem('userInfo'))
      ?.user?.isUserFullyActivated;

    if (isSchedulingPickupModalVisible) return null;
    return (
      <Modal
        {...this.props}
        wrapClassName="br-request-pickup-modal"
        title={null}
        width={null}
        footer={
          <>
            <BRButton
              disabled={isLoading}
              onClick={this.handleCancel}
              label={intl.formatMessage({
                id: 'common.cancel'
              })}
              type="basic"
            />

            <BRButton
              loading={isLoading}
              className="br-registration-form-card__submit-btn"
              form="requestPickupForm"
              type="primary"
              htmlType="submit"
              label={intl.formatMessage({
                id: `common.confirm`
              })}
            />
          </>
        }
        onCancel={this.handleCancel}
      >
        <BRContentHeader
          title={intl.formatMessage({
            id: pickupId
              ? `pickups.create_edit_pickup.pickup_edit_title`
              : `new_order.request_pickup_modal.title`
          })}
          subtitle={
            !pickupId &&
            intl.formatMessage({
              id: `new_order.request_pickup_modal.subtitle`
            })
          }
          isInternalComponent
        />
        <Form
          className="br-request-pickup-modal__form"
          onFinish={this.onFinish}
          id="requestPickupForm"
          ref={this.formRef}
        >
          {isPrepareOrdersBannerVisible && (
            <BRInfoBanner
              className="br-request-pickup-modal__info-banner"
              content={
                <div>
                  <div>
                    {intl.formatMessage({
                      id: `new_order.request_pickup_modal.info_banner.title`
                    })}
                  </div>
                  <div className="br-request-pickup-modal__info-banner__btns">
                    <BRButton
                      label={intl.formatMessage({
                        id: `new_order.request_pickup_modal.info_banner.secondary_btn`
                      })}
                      onClick={this.handlePrepareOrdersGuide}
                    />
                    <BRButton
                      type="treitary-gray"
                      label={intl.formatMessage({
                        id: `new_order.request_pickup_modal.info_banner.text_btn`
                      })}
                      onClick={this.handleDismissInfoBanner}
                    />
                  </div>
                </div>
              }
            />
          )}
          <div className="br-form-row">
            <Form.Item
              name="scheduledDate"
              label={intl.formatMessage({
                id: `new_order.request_pickup_modal.form_labels.pickup_date`
              })}
              rules={[{ required: isUserFullyActivated }]}
              initialValue={scheduledDate}
            >
              <DatePicker
                className="br-request-pickup-modal__form__date-picker"
                dropdownClassName="br-request-pickup-modal__form__date-picker-dropdown"
                placeholder={intl.formatMessage({
                  id: `new_order.request_pickup_modal.select`
                })}
                onChange={this.handleOnChangePickupDate}
                disabledDate={this.pickupDisabledDates}
                getPopupContainer={(trigger) => trigger.parentElement}
                showToday={false}
                renderExtraFooter={this.datePickerFooter}
                ref={this.datePickerRef}
                suffixIcon={<DatePickerIcon />}
                format={(value) => {
                  const day = checkDate(
                    this.handleDateAccordingToServer({ value })
                  );
                  return `${day ? `${day},` : ''} ${dayjs(value).format(
                    day ? DATE_FORMAT : DATE_FORMAT_WITH_DAY
                  )}`?.trim();
                }}
                dateRender={this.datePickerCellRender}
                allowClear={false}
              />
            </Form.Item>
          </div>
          {showRepeatedPickups && (
            <div className="br-request-pickup-modal__form__repeat-pickups-container">
              <Checkbox
                checked={repeatedPickup.isRepeated}
                onChange={() => {
                  this.setState({
                    repeatedPickup: {
                      ...repeatedPickup,
                      isRepeated: !repeatedPickup.isRepeated
                    }
                  });
                }}
              >
                {intl.formatMessage({
                  id: `pickups.actions.create_repeated`
                })}
              </Checkbox>
              <span className="br-request-pickup-modal__form__repeat-pickups-subtitle">
                {intl.formatMessage({
                  id: `new_order.request_pickup_modal.repeat_pickup_subtitle`
                })}
              </span>
              <div
                className={classNames(
                  'br-request-pickup-modal__form__repeat-pickups',
                  {
                    'br-request-pickup-modal__form__repeat-pickups__visible':
                      repeatedPickup?.isRepeated
                  }
                )}
              >
                <div className="br-request-pickup-modal__form__repeat-pickups-options">
                  <span className="br-request-pickup-modal__form__repeat-pickups-option__title subheading">
                    {intl.formatMessage({
                      id: `new_order.request_pickup_modal.every`
                    })}
                  </span>
                  <div className="br-request-pickup-modal__form__repeat-pickups__days">
                    {DAYS.map(({ value, label, labelAr }, index) => (
                      <Button
                        className={classNames({
                          'br-request-pickup-modal__form__repeat-pickups__days-selected':
                            repeatedPickup.days.includes(index),
                          'br-request-pickup-modal__form__repeat-pickups__days-one-selected':
                            repeatedPickup.days.includes(index) &&
                            repeatedPickup.days.length === 1
                        })}
                        key={value}
                        onClick={() => {
                          this.handleRepeatedPickupDayChange(index);
                        }}
                      >
                        {intl.locale === LOCALE.AR
                          ? labelAr
                          : label.substring(0, 3)}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="br-request-pickup-modal__form__repeat-pickups-options">
                  <span className="br-request-pickup-modal__form__repeat-pickups-option__title subheading">
                    {intl.formatMessage({
                      id: `new_order.request_pickup_modal.ends`
                    })}
                  </span>
                  <DatePicker
                    className="br-request-pickup-modal__form__date-picker"
                    dropdownClassName="br-request-pickup-modal__form__date-picker-dropdown"
                    placeholder={intl.formatMessage({
                      id: `new_order.request_pickup_modal.select`
                    })}
                    value={repeatedPickup.endDate}
                    onChange={this.handleOnChangeEndDate}
                    disabledDate={(current) =>
                      current < this.state.scheduledDate
                    }
                    getPopupContainer={(trigger) => trigger.parentElement}
                    showToday={false}
                    suffixIcon={<DatePickerIcon />}
                    allowClear={false}
                    format={(value) => {
                      const day = checkDate(value);
                      return `${day ? `${day},` : ''} ${dayjs(value).format(
                        day ? DATE_FORMAT : DATE_FORMAT_WITH_DAY
                      )}`;
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          <div className="br-form-row">
            <Form.Item
              name="locationId"
              label={intl.formatMessage({
                id: `new_order.request_pickup_modal.form_labels.pickup_location`
              })}
              rules={[{ required: isUserFullyActivated }]}
            >
              <Select
                className="br-pickups__pickup-settings__form__selector br-request-pickup-modal__form__select-location-prefix"
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) => {
                  return option.label
                    .toLowerCase()
                    .includes(input.toLowerCase());
                }}
                onChange={this.handlePickupLocationChange}
                getPopupContainer={(trigger) => trigger.parentElement}
                autoComplete={'' + Math.random()}
                disabled={isLoading}
                dropdownRender={this.renderLocationDropdown}
                virtual={false}
              >
                {pickupLocations?.map(
                  ({
                    locationName,
                    address: { city, district },
                    _id,
                    isDefault
                  }) => (
                    <Select.Option
                      key={_id}
                      value={_id}
                      label={`${locationName} ${city?.name} - ${district?.name}`}
                    >
                      <div className="br-create-edit-pickup__location">
                        <div className="br-create-edit-pickup__location-item body">
                          <div>{locationName}</div>
                          <div className="br-create-edit-pickup__location-city-area">
                            {city?.name} {`${district?.name ? ',' : ''}`}
                            {district?.name}
                          </div>
                        </div>
                        {isDefault && (
                          <div className="br-create-edit-pickup__location-default subheading">
                            {intl.formatMessage({
                              id: 'common.default'
                            })}
                          </div>
                        )}
                      </div>
                    </Select.Option>
                  )
                )}
              </Select>
            </Form.Item>
          </div>
          {pickupLocation?.cancelledPickupsCount >= 2 && (
            <CancelledPickupsBanner
              cancelledPickupsCount={pickupLocation.cancelledPickupsCount}
              handlePrepareOrdersGuide={this.handlePrepareOrdersGuide}
            />
          )}
          <div className="br-form-row">
            <Form.Item
              name="contactPerson"
              label={intl.formatMessage({
                id: `new_order.request_pickup_modal.form_labels.contact_person`
              })}
              rules={[{ required: isUserFullyActivated }]}
            >
              <Select
                className="br-pickups__pickup-settings__form__selector br-request-pickup-modal__form__select-profile-prefix"
                onChange={this.onChangeContactPerson}
                autoComplete={'' + Math.random()}
                disabled={isLoading}
                dropdownRender={this.renderContactPersonDropdown}
                virtual={false}
              >
                {pickupLocation?.contacts?.length &&
                  pickupLocation.contacts.map(
                    ({ name, phone, isDefault = false }) => (
                      <Select.Option key={name} value={name}>
                        <div className="br-create-edit-pickup__location">
                          <div className="br-create-edit-pickup__location-item body">
                            <div>{name}</div>
                            <div className="br-create-edit-pickup__location-city-area">
                              {phone}
                            </div>
                          </div>
                          {isDefault && (
                            <div className="br-create-edit-pickup__location-default subheading">
                              {intl.formatMessage({
                                id: 'common.default'
                              })}
                            </div>
                          )}
                        </div>
                      </Select.Option>
                    )
                  )}
              </Select>
            </Form.Item>
          </div>
          {businessInfo?.subAccounts?.length && (
            <div className="br-form-row">
              <Form.Item
                name="subAccountId"
                className="br-pickups__pickup-settings__form__notes-header"
                label={
                  <>
                    {intl.formatMessage({
                      id: 'order_types.sub_business_title'
                    })}
                    <span className="br-form-optional-label">
                      {intl.formatMessage({
                        id: 'form.optional_label'
                      })}
                    </span>
                  </>
                }
              >
                <Select
                  autoComplete={'' + Math.random()}
                  disabled={isLoading || pickupId}
                  placeholder={intl.formatMessage({
                    id: 'order_types.sub_business_placeholder'
                  })}
                >
                  {businessInfo?.subAccounts?.length &&
                    businessInfo?.subAccounts.map((account) => (
                      <Select.Option key={account?._id} value={account?._id}>
                        <div className="br-create-edit-pickup__location">
                          <div className="br-create-edit-pickup__location-item body">
                            <div>{account.name}</div>
                          </div>
                        </div>
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </div>
          )}

          {!repeatedPickup.isRepeated && (
            <div className="br-form-row">
              <Form.Item
                name="numberOfParcels"
                label={intl.formatMessage({
                  id: `new_order.request_pickup_modal.form_labels.number_of_parcels`
                })}
                rules={[
                  { required: true },
                  minNumberRule({
                    message: intl.formatMessage({
                      id: `new_order.request_pickup_modal.number_of_parcels_error_message`
                    }),
                    min: MINIMUM_VALUE_FOR_PARCELS_NUMBER
                  })
                ]}
              >
                <Input
                  disabled={isLoading}
                  type="number"
                  min="1"
                  size="large"
                  placeholder={intl.formatMessage({
                    id: `new_order.request_pickup_modal.number_of_parcels_placeholder`
                  })}
                />
              </Form.Item>
            </div>
          )}

          <BRButton
            type="link-color"
            label={intl.formatMessage({
              id: `new_order.request_pickup_modal.add_pickup_notes`
            })}
            className={classNames(
              'br-request-pickup-modal__form__add-notes-btn button-md',
              {
                'br-request-pickup-modal__form__add-notes-btn__hidden':
                  withNotes
              }
            )}
            onClick={() => this.setState({ withNotes: true })}
          />
          <div
            className={classNames(
              'br-request-pickup-modal__form__notes-container',
              {
                'br-request-pickup-modal__form__notes-container__visible':
                  withNotes
              }
            )}
          >
            <Form.Item
              className="br-pickups__pickup-settings__form__notes-header"
              label={
                <>
                  {intl.formatMessage({
                    id: `pickups.create_edit_pickup.form_labels.notes`
                  })}
                  <span className="br-form-optional-label">
                    {intl.formatMessage({
                      id: 'form.optional_label'
                    })}
                  </span>
                </>
              }
              name="notes"
            >
              <Input.TextArea
                className="br-pickups__pickup-settings__form__notes-field"
                placeholder={intl.formatMessage({
                  id: `new_order.request_pickup_modal.notes_placeholder`
                })}
                autoSize={{ minRows: 3, maxRows: 3 }}
              />
            </Form.Item>
          </div>
          {(bulkyOptions?.heavyBulky || bulkyOptions?.lightBulky) && (
            <Form.Item
              name="isBulkyPickup"
              valuePropName="checked"
              className="br-form-without-label br-request-pickup-modal__form__bulky"
            >
              <Checkbox>
                {intl.formatMessage({
                  id: `pickups.create_edit_pickup.form_labels.bulky_title`
                })}
                <div className="br-request-pickup-modal__form__package-type-subtitle body">
                  {intl.formatMessage({
                    id: `pickups.create_edit_pickup.form_labels.bulky_sub_title`
                  })}
                </div>
              </Checkbox>
            </Form.Item>
          )}
        </Form>
      </Modal>
    );
  }
}

export default injectIntl(withRouter(CreateEditPickupModal));
